import { useEffect, useRef, useState } from 'react';
import Dropzone from 'react-dropzone';
import Cropper from 'react-easy-crop';
import RModal from '../../../components/Rsuite/Modal';
import { useCustomTheme } from '../../../hooks/useCustomTheme';
import { CustomButton } from '../../../components/CustomButton';
import api from '../../../services/api';
import { toastSuccess, toastError } from '../../../toasts';
import { IMAGES_PATH } from '../../../utils/imagesPath';

const zoomOpcoes = {
  maximo: 3,
  minimo: 1,
  incremento: 0.25,
};

const ImagemPromocao = ({ modal, close, promocao }) => {
  const [imagem, setImagem] = useState(null);
  const [tipoImagem, setTipoImagem] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const croppedArea = useRef(null);
  const { theme } = useCustomTheme();

  function readFile(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => resolve(reader.result), false);
      reader.readAsDataURL(file);
    });
  }

  async function onDrop(acceptedFiles) {
    const file = acceptedFiles[0];
    setImagem(await readFile(file));
    setTipoImagem(file.type);
  }

  function onCropComplete(_, croppedAreaPixels) {
    croppedArea.current = croppedAreaPixels;
  }

  async function createImage(url) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });
  }

  async function getCroppedImg(imageSrc, crop) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = crop.width;
    canvas.height = crop.height;

    ctx.drawImage(
      image,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, tipoImagem);
    });
  }

  async function onSubmit() {
    if (!imagem) {
      toastError('Nenhuma imagem selecionada para salvar.');
      return;
    }

    try {
      const img = await getCroppedImg(imagem, croppedArea.current);
      const formData = new FormData();
      const fileExtension = tipoImagem
        ? tipoImagem.replace('image/', '')
        : 'jpeg';
      formData.append('image', img, `img.${fileExtension}`);

      await api.patch(`promocao/alterarImagem/${promocao.Id}`, formData);
      toastSuccess('Imagem salva com sucesso');
      close();
    } catch (error) {
      console.log({ error });
      toastError('Não foi possível salvar a imagem');
    }
  }

  function cancelar() {
    setImagem(null);
    setTipoImagem('');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    croppedArea.current = null;
  }

  useEffect(() => {
    if (modal && promocao?.Imagem) {
      const imageUrl = `${IMAGES_PATH}${promocao.Imagem}`;
      setImagem(imageUrl);
      setTipoImagem('');
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } else if (!modal) {
      setImagem(null);
    }
  }, [modal, promocao]);

  if (!modal) {
    return null;
  }

  return (
    <RModal
      open={modal}
      onClose={close}
      title={`Imagem da Promoção: ${promocao?.Titulo}`}
      footer={
        <div className="imagemControls">
          <CustomButton appearance="subtle" onClick={cancelar}>
            {imagem ? 'Alterar / Remover Imagem' : 'Cancelar Upload'}
          </CustomButton>
          {imagem && (
            <>
              <CustomButton
                appearance="ghost"
                disabled={zoom === zoomOpcoes.minimo}
                onClick={() =>
                  setZoom(
                    Math.max(zoom - zoomOpcoes.incremento, zoomOpcoes.minimo),
                  )
                }
              >
                Zoom -
              </CustomButton>
              <CustomButton
                appearance="ghost"
                disabled={zoom === zoomOpcoes.maximo}
                onClick={() =>
                  setZoom(
                    Math.min(zoom + zoomOpcoes.incremento, zoomOpcoes.maximo),
                  )
                }
              >
                Zoom +
              </CustomButton>
              <CustomButton appearance="primary" onClick={onSubmit}>
                Salvar Imagem
              </CustomButton>
            </>
          )}
        </div>
      }
    >
      <div
        className="modal-imagem-grupo-container"
        style={{ background: theme.colors.background }}
      >
        <div
          className="imagemContainer"
          style={{ background: theme.colors.background }}
        >
          {imagem ? (
            <Cropper
              image={imagem}
              crop={crop}
              zoom={zoom}
              aspect={4 / 5}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid
            />
          ) : (
            <Dropzone
              noClick={false}
              maxFiles={1}
              multiple={false}
              accept="image/*"
              onDropAccepted={onDrop}
            >
              {({
                getRootProps,
                getInputProps,
                isDragAccept,
                isDragReject,
              }) => (
                <div
                  {...getRootProps()}
                  className={`dropContainer ${
                    isDragAccept ? 'dragAccepted' : ''
                  } ${isDragReject ? 'dragReject' : ''}`}
                  style={{
                    background: theme.colors.background,
                    borderColor: theme.colors.text,
                  }}
                >
                  <input {...getInputProps()} />
                  <p className={'content'} style={{ color: theme.colors.text }}>
                    {isDragReject
                      ? 'Arquivo selecionado não é uma imagem válida'
                      : 'Arraste uma imagem ou clique para selecionar'}
                  </p>
                </div>
              )}
            </Dropzone>
          )}
        </div>
      </div>
    </RModal>
  );
};

export default ImagemPromocao;
